/**
 * Gallery Database Layer
 *
 * Pure IndexedDB operations via Dexie.js.
 * NO Svelte runes here - this is the boundary layer that handles IndexedDB directly.
 *
 * Schema:
 * - favorites: 'id, savedAt' (blob fields are NOT indexed)
 */

import Dexie from 'dexie';
import { isVideoSubmissionUnresolved, unresolvedVideoError } from '../utils/video-submission-journal.js';
import { isImageActionUnresolved, imageActionUnverified } from '../utils/image-actions.js';

export function createGalleryDatabase(name = 'SogniVibeGallery', options) {
  const database = new Dexie(name, options);
  database.version(1).stores({ favorites: 'id, savedAt' });
  // The existing favorites table keeps its keys and records during upgrade.
  // Blob fields are never indexed. Every new record is scoped to a wallet.
  database.version(2).stores({
    favorites: 'id, savedAt',
    sessions: '[accountId+id], accountId, [accountId+updatedAt]',
    assets: '[accountId+sessionId+id], accountId, [accountId+sessionId]',
    videos: '[accountId+favoriteId], accountId, favoriteId, savedAt'
  });
  database.version(3).stores({
    favorites: 'id, savedAt',
    sessions: '[accountId+id], accountId, [accountId+updatedAt]',
    assets: '[accountId+sessionId+id], accountId, [accountId+sessionId]',
    videos: '[accountId+favoriteId], accountId, favoriteId, savedAt',
    videoSubmissions: '[accountId+favoriteId], accountId'
  });
  database.version(4).stores({
    favorites: 'id, savedAt',
    sessions: '[accountId+id], accountId, [accountId+updatedAt]',
    assets: '[accountId+sessionId+id], accountId, [accountId+sessionId]',
    videos: '[accountId+favoriteId], accountId, favoriteId, savedAt',
    videoSubmissions: '[accountId+favoriteId], accountId',
    imageActions: '[accountId+favoriteId], accountId'
  });
  return database;
}

export const db = createGalleryDatabase();

// Optional handler invoked when the database is updated in another tab.
// The state layer registers this so it can surface a non-blocking error
// instead of forcing a page reload (which would kill in-progress session state).
let onVersionChange = null;
const versionChangeListeners = new Set();

export function onStorageVersionChange(handler) {
  versionChangeListeners.add(handler);
  return () => versionChangeListeners.delete(handler);
}

/**
 * Register a callback for IndexedDB versionchange events.
 * @param {(message: string) => void} handler
 */
export function setVersionChangeHandler(handler) {
  onVersionChange = handler;
}

// Handle version conflicts (e.g., another tab updates schema).
// Close the connection so the other tab can upgrade, then notify the app.
// Do NOT force-reload: that discards unsaved session state.
db.on('versionchange', () => {
  db.close();
  onVersionChange?.('Gallery was updated in another tab — refresh to reload your favorites');
  for (const handler of versionChangeListeners) handler('Saved sessions were updated in another tab. Keep this tab open and refresh after saving your work.');
});

/**
 * Save a favorite record to IndexedDB
 * @param {Object} record - The favorite record to save
 * @param {string} record.id - Unique identifier
 * @param {Blob} record.blob - Image blob data
 * @param {string} record.originalUrl - Original image URL
 * @param {string} record.prompt - Generation prompt
 * @param {string} record.model - Model used for generation
 * @param {string} [record.workerName] - Assigned render worker
 * @param {string} [record.projectId] - Sogni project identifier
 * @param {string} [record.jobId] - Sogni render job identifier
 * @param {number} record.batchIndex - Batch index in iteration
 * @param {number} record.imageIndex - Image index in batch
 * @param {number} record.savedAt - Timestamp when saved
 * @returns {Promise<string>} The id of the saved record
 */
export async function saveFavorite(record) {
  return await db.favorites.add(record);
}

/**
 * Get all favorites ordered by savedAt descending (newest first)
 * @returns {Promise<Array>} Array of favorite records
 */
export async function getAllFavorites() {
  return await db.favorites.orderBy('savedAt').reverse().toArray();
}

/**
 * Remove a favorite by id
 * @param {string} id - The id of the favorite to remove
 * @returns {Promise<void>}
 */
export async function removeFavorite(id) {
  return db.transaction('rw', db.favorites, db.videos, async () => {
    await db.favorites.delete(id);
    await db.videos.where('favoriteId').equals(id).delete();
  });
}

/**
 * Check if an image is already favorited
 * @param {string} id - The id to check
 * @returns {Promise<boolean>} True if exists, false otherwise
 */
export async function isFavorited(id) {
  const record = await db.favorites.get(id);
  return !!record;
}

export async function saveSession(record, assets, database = db) {
  return database.transaction('rw', database.sessions, database.assets, async () => {
    await database.sessions.put(record);
    if (assets.length) await database.assets.bulkPut(assets);
  });
}

export async function loadSession(accountId, id, database = db) {
  return database.transaction('r', database.sessions, database.assets, async () => {
    const record = id ? await database.sessions.get([accountId, id]) :
      await database.sessions.where('[accountId+updatedAt]')
        .between([accountId, Dexie.minKey], [accountId, Dexie.maxKey]).reverse().first();
    if (!record) return null;
    const assets = await database.assets.where('[accountId+sessionId]').equals([accountId, record.id]).toArray();
    return { record, assets };
  });
}

export async function listSessions(accountId, limit = 50, database = db) {
  const records = await database.sessions.where('[accountId+updatedAt]')
    .between([accountId, Dexie.minKey], [accountId, Dexie.maxKey]).reverse().limit(limit).toArray();
  return records.map(({ accountId: owner, id, title, updatedAt }) => ({ accountId: owner, id, title, updatedAt }));
}

export async function deleteSession(accountId, id, database = db) {
  return database.transaction('rw', database.sessions, database.assets, async () => {
    await database.sessions.delete([accountId, id]);
    await database.assets.where('[accountId+sessionId]').equals([accountId, id]).delete();
  });
}

export async function saveVideo(record, database = db) {
  return database.transaction('rw', database.favorites, database.videos, database.videoSubmissions, async () => {
    // A late completion cannot resurrect a favorite removed during generation.
    if (!await database.favorites.get(record.favoriteId)) return false;
    const submission = await database.videoSubmissions.get([record.accountId, record.favoriteId]);
    // Another tab may finish a newer take while this native download is pending.
    if (record.metadata?.videoAttemptId && submission?.attemptId !== record.metadata.videoAttemptId) return false;
    await database.videos.put(record);
    if (submission && submission.attemptId === record.metadata?.videoAttemptId) {
      await database.videoSubmissions.put({ ...submission, status: 'saved', updatedAt: Date.now() });
    }
    return true;
  });
}

export function loadVideos(accountId, database = db) {
  return database.videos.where('accountId').equals(accountId).toArray();
}

export function deleteVideo(accountId, favoriteId, database = db) {
  return database.videos.delete([accountId, favoriteId]);
}

export function loadVideoSubmissions(accountId, database = db) {
  return database.videoSubmissions.where('accountId').equals(accountId).toArray();
}

export function beginVideoSubmission(record, database = db) {
  return database.transaction('rw', database.videoSubmissions, async () => {
    const previous = await database.videoSubmissions.get([record.accountId, record.favoriteId]);
    if (isVideoSubmissionUnresolved(previous)) {
      throw Object.assign(unresolvedVideoError('An existing video submission is saved for this image. Load its saved record and check that project before generating again.', previous.projectId), { submission: previous });
    }
    await database.videoSubmissions.put(record);
  });
}

export function updateVideoSubmission(accountId, favoriteId, attemptId, changes, database = db) {
  return database.transaction('rw', database.videoSubmissions, async () => {
    const record = await database.videoSubmissions.get([accountId, favoriteId]);
    if (!record || record.attemptId !== attemptId) return false;
    if (!isVideoSubmissionUnresolved(record) && isVideoSubmissionUnresolved({ ...record, ...changes })) return true;
    await database.videoSubmissions.put({ ...record, ...changes, updatedAt: Date.now() });
    return true;
  });
}

export function loadImageAction(accountId, favoriteId, database = db) {
  return database.imageActions.get([accountId, favoriteId]);
}

/** The durable reservation prevents two tabs from submitting the same source action. */
export function beginImageAction(record, database = db) {
  return database.transaction('rw', database.imageActions, async () => {
    const previous = await database.imageActions.get([record.accountId, record.favoriteId]);
    if (isImageActionUnresolved(previous)) throw Object.assign(imageActionUnverified(
      'This image already has an unresolved action. Check its saved project before running another.', previous.projectId), { submission: previous });
    await database.imageActions.put(record);
  });
}

export function updateImageAction(record, changes, database = db) {
  return database.transaction('rw', database.imageActions, async () => {
    const current = await database.imageActions.get([record.accountId, record.favoriteId]);
    if (!current || current.attemptId !== record.attemptId) return false;
    if (!isImageActionUnresolved(current) && isImageActionUnresolved({ ...current, ...changes })) return true;
    await database.imageActions.put({ ...current, ...changes, updatedAt: Date.now() });
    return true;
  });
}

/** Persist the original result and terminal receipt in the same transaction. */
export function saveImageActionResult(record, favorite, database = db) {
  return database.transaction('rw', database.imageActions, database.favorites, async () => {
    const current = await database.imageActions.get([record.accountId, record.favoriteId]);
    if (!current || current.attemptId !== record.attemptId) return false;
    await database.favorites.put(favorite);
    await database.imageActions.put({ ...current, projectId: record.projectId, status: 'saved', resultFavoriteId: favorite.id, updatedAt: Date.now() });
    return true;
  });
}
